import React, { useCallback, useEffect, useMemo, useState } from "react";
import instance from "../../api/api";

const PAGE_SIZE = 50;

const ACTION_GROUPS = [
  {
    label: "Risks",
    actions: {
      RISK_CREATED: "Risk added",
      RISK_UPDATED: "Risk edited",
      RISK_DELETED: "Risk deleted",
      RISK_RATED: "Risk entry rated",
      STATUS_CHANGED: "Stage changed",
      ASSESSMENT_SUBMITTED: "Assessment submitted",
      ASSESSMENT_DELETED: "Assessment deleted",
      RESPONSE_SUBMITTED: "Response submitted",
      RESPONSE_RATED: "Response rated",
      MONITORING_PLAN_SAVED: "Monitoring plan saved",
      REASSESSMENT_SAVED: "Reassessment saved",
    },
  },
  {
    label: "Users & settings",
    actions: {
      USER_ROLE_CHANGED: "Admin access changed",
      USER_DEACTIVATED: "User deactivated",
      USER_ACTIVATED: "User reactivated",
      DEPARTMENT_CREATED: "Department added",
      DEPARTMENT_UPDATED: "Department edited",
      DEPARTMENT_DELETED: "Department deleted",
      PERSPECTIVES_UPDATED: "Perspectives & objectives edited",
    },
  },
];

const STATUS_LABELS = {
  SELECTION_COMPLETE: "Selected",
  COMPLETED: "Monitored",
  ACTIVE_MANAGEMENT: "Active monitoring",
};

const FIELD_LABELS = {
  riskNumber: "Risk number",
  riskName: "Name",
  description: "Description",
  source: "Source",
  rootCause: "Root cause",
  objective: "Objective",
  perspective: "Perspective",
  riskConsequence: "Consequence",
  responseText: "Response",
  name: "Name",
};

const statusLabel = (status) => {
  if (!status) return "—";
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  const words = status.toLowerCase().split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const fullName = (u) => (u ? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email : "Someone (user removed)");

const factorStrings = (factors) =>
  (Array.isArray(factors) ? factors : []).flatMap((f) =>
    f && typeof f === "object" ? Object.entries(f).map(([p, o]) => `${p}: ${o}`) : []
  );

const listDiff = (from, to) => {
  const before = new Set(from || []);
  const after = new Set(to || []);
  return {
    added: [...after].filter((x) => !before.has(x)),
    removed: [...before].filter((x) => !after.has(x)),
  };
};

// One readable sentence for an entry, minus the actor's name
function describe(entry) {
  const d = entry.details || {};
  const target = d.targetUserName || d.targetUserEmail || "a user";
  switch (entry.action) {
    case "RISK_CREATED": return "added this risk";
    case "RISK_UPDATED": return "edited this risk";
    case "RISK_DELETED": return "deleted this risk";
    case "RISK_RATED": return d.updated ? "updated their rating of the risk entry" : "rated the risk entry";
    case "STATUS_CHANGED":
      return `moved ${d.stage ? `the ${d.stage.toLowerCase()} ` : ""}from ${statusLabel(entry.fromStatus)} to ${statusLabel(entry.toStatus)}`;
    case "ASSESSMENT_SUBMITTED": return d.updated ? "updated their assessment" : "submitted an assessment";
    case "ASSESSMENT_DELETED": return "deleted an assessment";
    case "RESPONSE_SUBMITTED": return d.updated ? "edited the response" : "submitted the response";
    case "RESPONSE_RATED": return d.updated ? "updated their rating of the response" : "rated the response";
    case "MONITORING_PLAN_SAVED":
      return `${d.updated ? "updated" : "saved"} the monitoring plan${d.actionOwner ? ` (owner: ${d.actionOwner})` : ""}`;
    case "REASSESSMENT_SAVED":
      return `${d.updated ? "rescheduled" : "scheduled"} reassessment${d.reassessmentDate ? ` for ${d.reassessmentDate}` : ""}`;
    case "USER_ROLE_CHANGED":
      return entry.toStatus === "ADMIN" ? `made ${target} an admin` : `removed admin access from ${target}`;
    case "USER_DEACTIVATED": return `deactivated ${target}`;
    case "USER_ACTIVATED": return `reactivated ${target}`;
    case "DEPARTMENT_CREATED": return `added the ${d.name} department`;
    case "DEPARTMENT_UPDATED":
      return d.changes?.name
        ? `renamed the ${d.changes.name.from} department to ${d.changes.name.to}`
        : `edited the ${d.name} department`;
    case "DEPARTMENT_DELETED": return `deleted the ${d.name} department`;
    case "PERSPECTIVES_UPDATED": return "edited perspectives & objectives";
    default: return entry.action.toLowerCase().split("_").join(" ");
  }
}

// Extra lines shown under an entry: field changes, perspective/objective changes
function detailLines(entry) {
  const d = entry.details || {};
  if (d.changes && entry.action !== "DEPARTMENT_UPDATED") {
    return Object.entries(d.changes).map(([field, { from, to }]) => ({
      label: FIELD_LABELS[field] || field,
      from: from ?? "—",
      to: to ?? "—",
    }));
  }
  if (entry.action === "DEPARTMENT_UPDATED" && d.changes?.description) {
    return [{ label: "Description", from: d.changes.description.from ?? "—", to: d.changes.description.to ?? "—" }];
  }
  if (entry.action === "PERSPECTIVES_UPDATED") {
    const p = listDiff(d.from?.Perspective, d.to?.Perspective);
    const o = listDiff(factorStrings(d.from?.Factors), factorStrings(d.to?.Factors));
    return [
      ...p.added.map((x) => ({ label: "Perspective added", value: x })),
      ...p.removed.map((x) => ({ label: "Perspective removed", value: x })),
      ...o.added.map((x) => ({ label: "Objective added", value: x })),
      ...o.removed.map((x) => ({ label: "Objective removed", value: x })),
    ];
  }
  return [];
}

const dayKey = (iso) => new Date(iso).toDateString();
const dayLabel = (iso) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};
const timeLabel = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

function EntryRow({ entry, onPickRisk, showRisk }) {
  const lines = detailLines(entry);
  const risk = entry.RiskRegister;
  return (
    <li className="flex gap-4 px-5 py-3">
      <time dateTime={entry.createdAt} className="w-14 shrink-0 pt-0.5 text-xs tabular-nums text-gray-400">
        {timeLabel(entry.createdAt)}
      </time>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-800">
          <span className="font-semibold text-gray-900">{fullName(entry.User)}</span> {describe(entry)}
        </p>
        {showRisk && risk && (
          <button
            onClick={() => onPickRisk(risk)}
            title="Show only this risk's history"
            className="mt-1 inline-flex max-w-full items-center gap-1.5 rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-200"
          >
            <span className="font-mono font-semibold">{risk.riskNumber}</span>
            <span className="truncate">{risk.riskName}</span>
            {risk.isDeleted && <span className="text-red-600">(deleted)</span>}
          </button>
        )}
        {lines.length > 0 && (
          <ul className="mt-1.5 space-y-1 border-l-2 border-gray-100 pl-3">
            {lines.map((line, i) => (
              <li key={i} className="text-xs text-gray-600">
                <span className="font-semibold text-gray-700">{line.label}:</span>{" "}
                {"value" in line ? (
                  line.value
                ) : (
                  <>
                    <span className="text-gray-400 line-through">{String(line.from)}</span>{" "}
                    <span aria-label="changed to">→</span> {String(line.to)}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

export default function AuditTrail() {
  const [entries, setEntries] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [action, setAction] = useState("");
  const [risk, setRisk] = useState(null);
  const [search, setSearch] = useState("");

  const fetchPage = useCallback(
    async (cursor) => {
      const params = { limit: PAGE_SIZE };
      if (action) params.action = action;
      if (risk) params.riskRegisterId = risk.id;
      if (cursor) params.cursor = cursor;
      const res = await instance.get("/api/admin/audit", { params });
      return res.data;
    },
    [action, risk]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(null)
      .then((data) => {
        if (cancelled) return;
        setEntries(data.data || []);
        setNextCursor(data.nextCursor);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message ?? "Could not load the audit trail.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(nextCursor);
      setEntries((list) => [...list, ...(data.data || [])]);
      setNextCursor(data.nextCursor);
    } catch (err) {
      setError(err?.response?.data?.message ?? "Could not load more entries.");
    } finally {
      setLoadingMore(false);
    }
  };

  // Search narrows what's already loaded: person, risk number/name, or description
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [fullName(e.User), e.User?.email, e.RiskRegister?.riskNumber, e.RiskRegister?.riskName, describe(e)]
        .filter(Boolean)
        .some((text) => text.toLowerCase().includes(q))
    );
  }, [entries, search]);

  const days = useMemo(() => {
    const groups = [];
    for (const entry of visible) {
      const key = dayKey(entry.createdAt);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.entries.push(entry);
      else groups.push({ key, label: dayLabel(entry.createdAt), entries: [entry] });
    }
    return groups;
  }, [visible]);

  const filtered = Boolean(action || risk || search.trim());

  return (
    <div className="max-w-4xl space-y-4 pb-10">
      <p className="max-w-2xl text-sm text-gray-500">
        Every change to a risk, and to users and settings, with who made it and when. Entries can't be edited or removed.
      </p>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search person or risk…"
          aria-label="Search the audit trail"
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-caribbeanCurrent focus:outline-none focus:ring-1 focus:ring-caribbeanCurrent sm:w-64"
        />
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          aria-label="Filter by type of change"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-caribbeanCurrent focus:outline-none focus:ring-1 focus:ring-caribbeanCurrent"
        >
          <option value="">All changes</option>
          {ACTION_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {Object.entries(group.actions).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        {risk && (
          <span className="inline-flex items-center gap-2 rounded-lg bg-caribbeanCurrent/10 px-3 py-1.5 text-sm text-gray-800">
            <span>
              History of <span className="font-mono font-semibold">{risk.riskNumber}</span> {risk.riskName}
            </span>
            <button
              onClick={() => setRisk(null)}
              aria-label="Show all risks"
              className="rounded px-1 font-bold text-gray-500 hover:bg-white hover:text-gray-900"
            >
              ×
            </button>
          </span>
        )}
        {filtered && (
          <button
            onClick={() => { setAction(""); setRisk(null); setSearch(""); }}
            className="text-sm font-semibold text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Entries */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="space-y-2 p-5">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-100" />)}
          </div>
        ) : error && entries.length === 0 ? (
          <p className="p-5 text-sm text-red-600">{error}</p>
        ) : days.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm font-semibold text-gray-700">
              {filtered ? "No entries match these filters." : "Nothing recorded yet."}
            </p>
            {!filtered && (
              <p className="mt-1 text-sm text-gray-500">Changes made from now on will appear here.</p>
            )}
          </div>
        ) : (
          days.map((day) => (
            <section key={day.key} aria-label={day.label}>
              <h3 className="sticky top-0 border-b border-gray-100 bg-gray-50 px-5 py-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 first:rounded-t-xl">
                {day.label}
              </h3>
              <ul className="divide-y divide-gray-50">
                {day.entries.map((entry) => (
                  <EntryRow key={entry.id} entry={entry} onPickRisk={setRisk} showRisk={!risk} />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>

      {!loading && nextCursor && (
        <div className="flex flex-col items-center gap-1">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded-lg border border-gray-200 bg-white px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : "Load older entries"}
          </button>
          {search.trim() && <p className="text-xs text-gray-400">Search only covers entries loaded so far.</p>}
        </div>
      )}
      {error && entries.length > 0 && <p className="text-center text-sm text-red-600">{error}</p>}
    </div>
  );
}
